import { assert } from '@libraries/typescript/utilities';

export type AdminAgentPageContextEnvironment =
    | 'production'
    | 'staging'
    | 'development';

export type AdminAgentTracePageContext = {
    readonly page_kind: 'agent_trace';
    readonly trace_id: string;
    readonly conversation_id: string;
    readonly run_id: string;
    readonly environment: AdminAgentPageContextEnvironment;
    readonly page_url: string;
};

export type AdminAgentPageContext = AdminAgentTracePageContext;

export type AdminAgentPageContextUnavailableReason =
    | 'unsupported_page'
    | 'missing_trace_id'
    | 'missing_run_id';

export type AdminAgentPageContextResolution =
    | {
          readonly status: 'available';
          readonly context: AdminAgentPageContext;
      }
    | {
          readonly status: 'unavailable';
          readonly reason: AdminAgentPageContextUnavailableReason;
          readonly context: null;
      };

export type AdminAgentPageContextResolverInput = {
    readonly page_kind: string;
    readonly [key: string]: unknown;
};

export type AdminAgentPageContextResolver = {
    readonly page_kind: string;
    readonly resolve: (
        input: AdminAgentPageContextResolverInput,
    ) => AdminAgentPageContextResolution;
};

export type AdminAgentPageContextResolverRegistry = {
    readonly resolve: (
        input: AdminAgentPageContextResolverInput,
    ) => AdminAgentPageContextResolution;
};

export function createAdminAgentPageContextResolverRegistry(
    resolvers: Array<AdminAgentPageContextResolver>,
): AdminAgentPageContextResolverRegistry {
    return {
        resolve: (input) => {
            const resolver = resolvers.find(
                (candidate) => candidate.page_kind === input.page_kind,
            );
            if (!resolver) {
                return {
                    status: 'unavailable',
                    reason: 'unsupported_page',
                    context: null,
                };
            }
            return resolver.resolve(input);
        },
    };
}

export function normalizeAdminAgentPageContextEnvironment(
    environment: unknown,
): AdminAgentPageContextEnvironment {
    switch (environment) {
        case 'production':
        case 'staging':
        case 'development':
            return environment;
        default:
            return 'development';
    }
}

export function requireAdminAgentPageContextInputString(
    value: unknown,
    reason: AdminAgentPageContextUnavailableReason,
):
    | { readonly status: 'available'; readonly value: string }
    | {
          readonly status: 'unavailable';
          readonly reason: AdminAgentPageContextUnavailableReason;
      } {
    if (typeof value !== 'string' || value.trim() === '') {
        return {
            status: 'unavailable',
            reason,
        };
    }

    return {
        status: 'available',
        value,
    };
}

export function assertAdminAgentPageContext(
    resolution: AdminAgentPageContextResolution,
): AdminAgentPageContext | null {
    switch (resolution.status) {
        case 'available':
            return resolution.context;
        case 'unavailable':
            return null;
        default:
            return assert.never(resolution);
    }
}
